import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { useMessaging } from "../../contexts/MessagingContext";
import { useAuth } from "../../contexts/AuthContext";
import { Conversation } from "../../types";
import {
  formatRelativeTime,
  getUserDisplayName,
  getUnreadCount,
  formatMessagePreview,
  sortConversations,
} from "../../utils/messageUtils";
import { useSellerPresence } from "../../hooks/useSellerPresence";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import ProfilePicture from "../ProfilePicture";
import {
  ConversationListSkeleton,
  InitializationSkeleton,
} from "./LoadingSkeletons";
import ErrorState from "../common/ErrorState";
import {
  MessageCircle,
  Search,
  ArrowLeft,
  Users,
  MoreVertical,
  Ban,
  ExternalLink,
  X,
  ChevronLeft,
  ChevronRight,
  Tag,
  ClipboardList,
  Package,
  ChevronDown,
  Truck,
  ArrowRight,
} from "lucide-react";
import { isValidDocumentId } from "../../utils/validation";
import { messagingService, rewardsService } from "../../services/firebaseConfig";
import { getUserOrders } from "../../services/orderService";
import { toast } from "react-hot-toast";
import CouponCard from "../CouponCard";
import { Voucher, UserVoucher } from "../../types";
import { useScrollLock } from "../../hooks/useScrollLock";
import { useViewport } from "../../hooks/useViewport";
import { Z_INDEX, DRAWER_SIZING } from "../../utils/constants";

interface MessagingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  widgetMode?: boolean; // When true, renders without backdrop and uses widget positioning
}

const MessagingDrawer: React.FC<MessagingDrawerProps> = ({ isOpen, onClose, widgetMode = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const viewport = useViewport();

  // Lock scroll when drawer is open
  useScrollLock(isOpen);
  const {
    conversations,
    selectedConversation,
    messages,
    loading,
    error,
    isOtherUserTyping,
    isSending,
    isInitialized,
    hasMore,
    loadingMore,
    filteredConversations,
    setSelectedConversation,
    sendMessage,
    markConversationRead,
    getOrCreateConversation,
    loadMoreMessages,
    handleTyping,
    retryFailedMessage,
  } = useMessaging();

  // Local UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isOrdersExpanded, setIsOrdersExpanded] = useState(false);
  const [isPromotionsExpanded, setIsPromotionsExpanded] = useState(false);
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [availableVouchers, setAvailableVouchers] = useState<Voucher[]>([]);
  const [userVouchers, setUserVouchers] = useState<UserVoucher[]>([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [claimingVoucher, setClaimingVoucher] = useState<string | null>(null);
  const sellerIdParam = searchParams.get("seller");
  const hasProcessedSellerParam = useRef(false);

  // Get other user from selected conversation
  const otherUser = selectedConversation?.otherUser;
  const otherUserId =
    otherUser?.uid ||
    selectedConversation?.participants?.find((id) => id !== currentUser?.uid);

  // Convert otherUser to the format expected by MessageList
  const messageListOtherUser = otherUser
    ? {
        uid: otherUser.uid,
        photoURL: otherUser.photoURL,
        displayName:
          otherUser.displayName ||
          otherUser.firstName ||
          otherUser.lastName ||
          undefined,
      }
    : undefined;

  // Seller presence for selected conversation
  const { sellerPresence } = useSellerPresence(otherUserId);

  // Filter conversations based on search
  const filteredConvs = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return sortConversations(filteredConversations);
    }

    const term = searchTerm.toLowerCase().trim();
    return sortConversations(
      filteredConversations.filter((conv) => {
        const displayName = getUserDisplayName(conv.otherUser).toLowerCase();
        const lastMessage = (conv.lastMessage || "").toLowerCase();
        return displayName.includes(term) || lastMessage.includes(term);
      })
    );
  }, [filteredConversations, searchTerm]);

  // Handle URL parameter for seller
  useEffect(() => {
    if (!isOpen) return;
    if (!currentUser?.uid || !sellerIdParam || hasProcessedSellerParam.current)
      return;
    if (!isInitialized) return;

    const processSellerParam = async () => {
      try {
        if (!isValidDocumentId(sellerIdParam)) {
          return;
        }

        const existingConv = conversations.find((conv) =>
          conv.participants.includes(sellerIdParam)
        );

        if (existingConv) {
          setSelectedConversation(existingConv);
          setShowMobileChat(true);
          hasProcessedSellerParam.current = true;
          return;
        }

        const newConv = await getOrCreateConversation(sellerIdParam);
        if (newConv) {
          setSelectedConversation(newConv);
          setShowMobileChat(true);
          hasProcessedSellerParam.current = true;
        }
      } catch {
        // Silent fail
      }
    };

    processSellerParam();
  }, [
    isOpen,
    currentUser?.uid,
    sellerIdParam,
    conversations,
    isInitialized,
    getOrCreateConversation,
    setSelectedConversation,
  ]);

  // Reset when drawer closes
  useEffect(() => {
    if (!isOpen) {
      hasProcessedSellerParam.current = false;
      setShowMobileChat(false);
      setSearchTerm("");
      setIsOrdersExpanded(false);
    }
  }, [isOpen]);

  // Fetch recent orders when drawer opens
  useEffect(() => {
    if (!isOpen || !currentUser?.uid) return;

    const fetchOrders = async () => {
      setOrdersLoading(true);
      try {
        const orders = await getUserOrders(currentUser.uid);
        // Get only the 3 most recent orders
        setRecentOrders(orders.slice(0, 3));
      } catch {
        // Silent fail
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [isOpen, currentUser?.uid]);

  // Fetch coupons when promotions section is expanded
  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;

    const fetchCoupons = async () => {
      if (!isPromotionsExpanded || !currentUser?.uid) {
        return;
      }

      try {
        if (isMounted) {
          setCouponsLoading(true);
        }
        
        // Add small delay to prevent concurrent calls
        await new Promise(resolve => {
          timeoutId = setTimeout(resolve, 100);
        });

        if (!isMounted) return;
        
        // Fetch available vouchers
        const available = await rewardsService.getActiveVouchers();
        if (isMounted) {
          setAvailableVouchers(available);
        }

        if (!isMounted) return;

        // Fetch user's claimed vouchers
        const userVouchersData = await rewardsService.getUserVouchers(currentUser.uid);
        if (isMounted) {
          setUserVouchers(userVouchersData);
        }
      } catch (error: any) {
        // SECURITY: Removed console.error to prevent information leakage
        if (isMounted) {
          toast.error('Failed to load coupons');
        }
      } finally {
        if (isMounted) {
          setCouponsLoading(false);
        }
      }
    };

    fetchCoupons();

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isPromotionsExpanded, currentUser?.uid]);

  // Filter active (non-expired, non-used) user vouchers
  const activeUserVouchers = userVouchers.filter((uv) => {
    // Exclude used vouchers
    if (uv.isUsed) return false;
    
    // Check expiration - exclude expired vouchers
    const now = new Date();
    const expiresAt = uv.expiresAt?.toDate?.() || uv.voucher?.expiresAt?.toDate?.() || null;
    if (expiresAt && expiresAt < now) {
      return false;
    }
    
    return true;
  });

  // Handle voucher claiming
  const handleClaimVoucher = async (voucherId: string) => {
    if (!currentUser?.uid) {
      toast.error('Please log in to claim coupons');
      return;
    }

    try {
      setClaimingVoucher(voucherId);
      await rewardsService.claimVoucher(currentUser.uid, voucherId);
      toast.success('Coupon claimed successfully!');
      
      // Refresh user vouchers
      const updatedUserVouchers = await rewardsService.getUserVouchers(currentUser.uid);
      setUserVouchers(updatedUserVouchers);
    } catch (error: any) {
      // SECURITY: Removed console.error to prevent information leakage
      toast.error(error.message || 'Failed to claim coupon');
    } finally {
      setClaimingVoucher(null);
    }
  };

  // Mark conversation as read when selected
  useEffect(() => {
    if (selectedConversation && currentUser?.uid && isOpen) {
      markConversationRead(selectedConversation.id, currentUser.uid).catch(
        () => {}
      );
    }
  }, [selectedConversation?.id, currentUser?.uid, markConversationRead, isOpen]);

  // Handle conversation selection
  const handleSelectConversation = useCallback(
    (conversation: Conversation) => {
      setSelectedConversation(conversation);
      setShowMobileChat(true);
      if (searchParams.has("seller")) {
        setSearchParams({});
      }
    },
    [setSelectedConversation, searchParams, setSearchParams]
  );

  // Handle sending message
  const handleSendMessage = useCallback(
    async (text: string, file?: File) => {
      if (!selectedConversation) return;

      try {
        await sendMessage(text, file);
      } catch {
        // Error handled by context
      }
    },
    [selectedConversation, sendMessage]
  );

  // Handle back button (mobile and desktop)
  const handleBack = useCallback(() => {
    setShowMobileChat(false);
    setSelectedConversation(null);
    // Preserve messages=open param, only remove seller param if it exists
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("seller");
    setSearchParams(newParams, { replace: true });
  }, [setSelectedConversation, setSearchParams, searchParams]);

  // Navigate to seller store page
  const handleViewSellerPage = useCallback(() => {
    if (otherUserId) {
      onClose();
      navigate(`/store-page/${otherUserId}`);
    }
    setShowMenu(false);
  }, [navigate, otherUserId, onClose]);

  // Block user functionality
  const handleBlockUser = useCallback(async () => {
    if (!selectedConversation?.id || !otherUserId) return;

    setIsBlocking(true);
    try {
      await messagingService.blockUser(selectedConversation.id, otherUserId);
      toast.success(`${getUserDisplayName(otherUser)} has been blocked`);
      setShowMenu(false);
      handleBack();
    } catch {
      toast.error("Failed to block user. Please try again.");
    } finally {
      setIsBlocking(false);
    }
  }, [selectedConversation?.id, otherUserId, otherUser, handleBack]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Handle mobile keyboard appearance - adjust drawer height
  useEffect(() => {
    if (!isOpen || !viewport.isMobile) return;

    const handleVisualViewportChange = () => {
      if (drawerRef.current && window.visualViewport) {
        // Adjust drawer height when keyboard appears
        const visualHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        
        if (visualHeight < windowHeight) {
          // Keyboard is visible
          drawerRef.current.style.height = `${visualHeight}px`;
        } else {
          // Keyboard is hidden
          drawerRef.current.style.height = '100vh';
        }
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
      return () => {
        window.visualViewport?.removeEventListener('resize', handleVisualViewportChange);
      };
    }
  }, [isOpen, viewport.isMobile]);

  // Handle swipe gesture to close on mobile
  useEffect(() => {
    if (!isOpen || !viewport.isMobile || !drawerRef.current) return;

    const drawer = drawerRef.current;
    let startY = 0;
    let startX = 0;
    let currentY = 0;
    let currentX = 0;
    let isDragging = false;
    const SWIPE_THRESHOLD = 100; // Minimum distance to trigger close
    const VELOCITY_THRESHOLD = 0.5; // Minimum velocity to trigger close

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      startY = touch.clientY;
      startX = touch.clientX;
      currentY = startY;
      currentX = startX;
      isDragging = true;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      
      const touch = e.touches[0];
      currentY = touch.clientY;
      currentX = touch.clientX;
      
      const deltaY = currentY - startY;
      const deltaX = currentX - startX;
      
      // Only allow swipe down or right to close
      if (deltaY > 0 && Math.abs(deltaY) > Math.abs(deltaX)) {
        // Swipe down - prevent default scrolling
        e.preventDefault();
        // Apply transform for visual feedback
        const progress = Math.min(deltaY / SWIPE_THRESHOLD, 1);
        drawer.style.transform = `translateY(${deltaY}px)`;
        drawer.style.opacity = `${1 - progress * 0.5}`;
      } else if (deltaX > 0 && Math.abs(deltaX) > Math.abs(deltaY)) {
        // Swipe right - prevent default scrolling
        e.preventDefault();
        const progress = Math.min(deltaX / SWIPE_THRESHOLD, 1);
        drawer.style.transform = `translateX(${deltaX}px)`;
        drawer.style.opacity = `${1 - progress * 0.5}`;
      }
    };

    const handleTouchEnd = () => {
      if (!isDragging) return;
      
      const deltaY = currentY - startY;
      const deltaX = currentX - startX;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const velocity = distance / 300; // Approximate velocity
      
      // Reset transform
      drawer.style.transform = '';
      drawer.style.opacity = '';
      
      // Close if threshold met
      if (
        (deltaY > SWIPE_THRESHOLD || deltaX > SWIPE_THRESHOLD) ||
        (distance > SWIPE_THRESHOLD * 0.5 && velocity > VELOCITY_THRESHOLD)
      ) {
        onClose();
      }
      
      isDragging = false;
    };

    drawer.addEventListener('touchstart', handleTouchStart, { passive: false });
    drawer.addEventListener('touchmove', handleTouchMove, { passive: false });
    drawer.addEventListener('touchend', handleTouchEnd);

    return () => {
      drawer.removeEventListener('touchstart', handleTouchStart);
      drawer.removeEventListener('touchmove', handleTouchMove);
      drawer.removeEventListener('touchend', handleTouchEnd);
      // Reset any transforms
      drawer.style.transform = '';
      drawer.style.opacity = '';
    };
  }, [isOpen, viewport.isMobile, onClose]);

  // Calculate total unread messages
  const totalUnread = filteredConversations.reduce(
    (acc, conv) => acc + getUnreadCount(conv, currentUser?.uid || ""),
    0
  );

  // Render content based on state
  const renderContent = () => {
    // Show initialization skeleton
    if (!isInitialized && loading) {
      return <InitializationSkeleton />;
    }

    // Show error state
    if (error && !isInitialized) {
      return (
        <div className="flex items-center justify-center h-full p-6">
          <ErrorState message={error} />
        </div>
      );
    }

    // Require authentication
    if (!currentUser) {
      return (
        <div className="flex items-center justify-center h-full p-6">
          <div className="text-center max-w-sm">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-7 h-7 text-gray-400" />
            </div>
            <h2 className="text-lg font-medium text-gray-800 mb-2">
              Sign in required
            </h2>
            <p className="text-gray-500 mb-4 text-sm">
              Please sign in to access your messages.
            </p>
            <button
              onClick={() => {
                onClose();
                navigate("/login");
              }}
              className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm font-medium"
            >
              Sign In
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col md:flex-row overflow-hidden" style={{ height: '100%', display: 'flex' }}>
        {/* Sidebar - Conversation List - Hidden when conversation is selected */}
        {!selectedConversation && (
          <div
            className={`
              ${showMobileChat ? "hidden" : "flex"} 
              md:flex flex-col w-full md:w-auto bg-white flex-shrink-0 border-r border-gray-100 h-full md:h-auto
              ${isSidebarCollapsed ? "md:w-[72px]" : "md:w-[320px]"}
            `}
          >
          {/* Sidebar Header - Light Theme */}
          <div className="bg-white border-b border-gray-100">
            <div className={`flex items-center justify-between ${isSidebarCollapsed ? "px-3 py-3" : "px-4 py-3"}`}>
              {!isSidebarCollapsed && (
                <h1 className="text-lg font-semibold text-gray-900">Messages</h1>
              )}
              <div className="flex items-center gap-1">
                {totalUnread > 0 && !isSidebarCollapsed && (
                  <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {totalUnread}
                  </span>
                )}
                {/* Expand/Collapse Toggle - Desktop only */}
                <button
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  className="hidden md:flex p-2.5 rounded-lg hover:bg-gray-100 text-gray-500 min-h-[44px] min-w-[44px] items-center justify-center"
                  title={isSidebarCollapsed ? "Expand" : "Collapse"}
                  aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                  {isSidebarCollapsed ? (
                    <ChevronRight className="w-5 h-5" />
                  ) : (
                    <ChevronLeft className="w-5 h-5" />
                  )}
                </button>
                {/* Close button - Mobile only */}
                <button
                  onClick={onClose}
                  className="md:hidden p-2.5 rounded-lg hover:bg-gray-100 text-gray-500 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Close messaging drawer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            {/* Search - hidden when collapsed */}
            {!isSidebarCollapsed && (
              <div className="px-4 pb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 bg-gray-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:bg-white border border-gray-100 placeholder-gray-400"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Category Sections - Only when expanded */}
          {!isSidebarCollapsed && (
            <div className="border-b border-gray-100">
              {/* Promotions - Expandable */}
              <div className="border-b border-gray-50">
                <button
                  onClick={() => setIsPromotionsExpanded(!isPromotionsExpanded)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
                >
                  <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                    <Tag className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-900">Promotions</p>
                    <p className="text-xs text-gray-500">
                      {availableVouchers.length > 0 
                        ? `${availableVouchers.length} coupon${availableVouchers.length > 1 ? 's' : ''} available`
                        : 'Don\'t miss out on exclusive promotions.'}
                    </p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isPromotionsExpanded ? 'rotate-180' : ''}`} />
                </button>

                {/* Expanded Promotions/Coupons List */}
                {isPromotionsExpanded && (
                  <div className="bg-gray-50 px-4 py-3 max-h-[400px] overflow-y-auto">
                    {couponsLoading ? (
                      <div className="py-3 text-center">
                        <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                      </div>
                    ) : availableVouchers.length > 0 ? (
                      <>
                        {/* Available Coupons (Unclaimed) */}
                        <div className="mb-4">
                          <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                            Available to Collect
                          </h4>
                          <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 scrollbar-hide -mx-1 px-1">
                            {availableVouchers
                              .filter(voucher => !activeUserVouchers.some(uv => uv.voucherId === voucher.id))
                              .map((voucher) => (
                                <div key={voucher.id} className="flex-shrink-0 min-w-[200px] max-w-[200px]">
                                  <CouponCard
                                    voucher={voucher}
                                    isClaimed={false}
                                    onClaim={handleClaimVoucher}
                                    isClaiming={claimingVoucher === voucher.id}
                                    compact={true}
                                  />
                                </div>
                              ))}
                          </div>
                        </div>

                        {/* Collected Coupons */}
                        {activeUserVouchers.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                              Collected
                            </h4>
                            <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 scrollbar-hide -mx-1 px-1">
                              {activeUserVouchers.map((userVoucher) => (
                                <div key={userVoucher.id} className="flex-shrink-0 min-w-[200px] max-w-[200px]">
                                  <CouponCard
                                    voucher={userVoucher.voucher}
                                    isClaimed={true}
                                    isClaiming={false}
                                    compact={true}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* View All Button */}
                        <button
                          onClick={() => {
                            onClose();
                            navigate("/vouchers");
                          }}
                          className="w-full mt-3 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <span>View All Coupons</span>
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <div className="py-4 text-center">
                        <p className="text-sm text-gray-500 mb-3">No coupons available at the moment.</p>
                        <button
                          onClick={() => {
                            onClose();
                            navigate("/vouchers");
                          }}
                          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
                        >
                          View All Coupons
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Orders & Shipping - Expandable */}
              <div className="border-b border-gray-50">
                <button
                  onClick={() => setIsOrdersExpanded(!isOrdersExpanded)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
                >
                  <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                    <ClipboardList className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-900">Orders & Shipping</p>
                    <p className="text-xs text-gray-500">
                      {recentOrders.length > 0 ? `${recentOrders.length} recent order${recentOrders.length > 1 ? 's' : ''}` : 'Keep up with your orders.'}
                    </p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOrdersExpanded ? 'rotate-180' : ''}`} />
                </button>

                {/* Expanded Orders List */}
                {isOrdersExpanded && (
                  <div className="bg-gray-50 px-4 py-2">
                    {ordersLoading ? (
                      <div className="py-3 text-center">
                        <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                      </div>
                    ) : recentOrders.length > 0 ? (
                      <>
                        {recentOrders.map((order) => (
                          <button
                            key={order.id}
                            onClick={() => {
                              onClose();
                              navigate(`/order-tracking?orderId=${order.id}`);
                            }}
                            className="w-full flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-white text-left"
                          >
                            <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                              {order.status === 'shipped' || order.status === 'delivered' ? (
                                <Truck className="w-4 h-4 text-green-500" />
                              ) : (
                                <Package className="w-4 h-4 text-orange-500" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-800 truncate">
                                Order #{order.id?.slice(-6).toUpperCase()}
                              </p>
                              <p className="text-[11px] text-gray-500 capitalize">
                                {order.status || 'Processing'} • {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </button>
                        ))}
                        <button
                          onClick={() => {
                            onClose();
                            navigate("/order-tracking");
                          }}
                          className="w-full text-center text-xs text-orange-600 font-medium py-2 hover:text-orange-700"
                        >
                          View all orders →
                        </button>
                      </>
                    ) : (
                      <div className="py-3 text-center">
                        <p className="text-xs text-gray-500">No orders yet</p>
                        <button
                          onClick={() => {
                            onClose();
                            navigate("/");
                          }}
                          className="text-xs text-orange-600 font-medium mt-1 hover:text-orange-700"
                        >
                          Start shopping →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Collapsed Category Icons */}
          {isSidebarCollapsed && (
            <div className="border-b border-gray-100 py-2">
              <button
                onClick={() => {
                  onClose();
                  navigate("/promotions");
                }}
                className="w-full flex justify-center py-2 hover:bg-gray-50"
                title="Promotions"
              >
                <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
                  <Tag className="w-5 h-5 text-white" />
                </div>
              </button>
              <button
                onClick={() => {
                  onClose();
                  navigate("/order-tracking");
                }}
                className="w-full flex justify-center py-2 hover:bg-gray-50 relative"
                title="Orders & Shipping"
              >
                <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-white" />
                </div>
                {recentOrders.length > 0 && (
                  <span className="absolute top-1 right-3 min-w-[16px] h-[16px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {recentOrders.length}
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {loading && !isInitialized ? (
              <ConversationListSkeleton />
            ) : filteredConvs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                {!isSidebarCollapsed ? (
                  <>
                    {/* Chat bubble illustration */}
                    <div className="w-20 h-20 mb-4 relative">
                      <div className="absolute inset-0 bg-gray-100 rounded-full"></div>
                      <div className="absolute inset-3 flex items-center justify-center">
                        <MessageCircle className="w-10 h-10 text-gray-300" />
                      </div>
                    </div>
                    <p className="text-sm font-medium text-gray-600">
                      {searchTerm ? "No results found" : "No chat history yet"}
                    </p>
                    {!searchTerm && (
                      <p className="text-xs text-gray-400 mt-1">
                        Start chatting with sellers
                      </p>
                    )}
                  </>
                ) : (
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-gray-300" />
                  </div>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {filteredConvs.map((conversation) => {
                  // Inside sidebar, no conversation is selected (sidebar only shows when !selectedConversation)
                  const isSelected = false;
                  const unreadCount = getUnreadCount(
                    conversation,
                    currentUser.uid
                  );
                  const displayName = getUserDisplayName(
                    conversation.otherUser
                  );
                  const lastMessagePreview = formatMessagePreview(
                    conversation.lastMessage || ""
                  );
                  const lastMessageTime = formatRelativeTime(
                    conversation.lastMessageTime
                  );

                  return (
                    <button
                      key={conversation.id}
                      onClick={() => handleSelectConversation(conversation)}
                      className={`w-full text-left hover:bg-gray-50 ${
                        isSidebarCollapsed ? "px-4 py-3 justify-center" : "px-4 py-3"
                      } ${
                        isSelected
                          ? "bg-orange-50"
                          : ""
                      }`}
                    >
                      <div className={`flex items-center ${isSidebarCollapsed ? "justify-center" : "gap-3"}`}>
                        <div className="relative flex-shrink-0">
                          <ProfilePicture
                            src={conversation.otherUser?.photoURL}
                            alt={displayName}
                            size="sm"
                            className="w-10 h-10 rounded-full"
                            user={conversation.otherUser}
                            userId={conversation.otherUser?.uid}
                          />
                          {/* Unread badge on avatar */}
                          {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                              {unreadCount > 9 ? "9+" : unreadCount}
                            </span>
                          )}
                        </div>
                        {/* Full info - hidden when collapsed */}
                        {!isSidebarCollapsed && (
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span
                                className={`text-sm truncate ${
                                  unreadCount > 0
                                    ? "font-semibold text-gray-900"
                                    : "font-medium text-gray-700"
                                }`}
                              >
                                {displayName}
                              </span>
                              <span
                                className={`text-[11px] flex-shrink-0 ml-2 ${
                                  unreadCount > 0
                                    ? "text-orange-500 font-medium"
                                    : "text-gray-400"
                                }`}
                              >
                                {lastMessageTime}
                              </span>
                            </div>
                            <div className="flex items-center justify-between mt-0.5">
                              <span
                                className={`text-xs truncate ${
                                  unreadCount > 0
                                    ? "text-gray-700"
                                    : "text-gray-500"
                                }`}
                              >
                                {lastMessagePreview || "Start a conversation"}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        )}

        {/* Main Chat Area - Full width when conversation is selected */}
        <div
          className={`
            ${selectedConversation ? "flex" : (showMobileChat ? "flex" : "hidden")} 
            ${selectedConversation ? "w-full" : ""}
            md:flex flex-1 flex-col bg-white min-h-0 h-full md:h-auto
          `}
        >
          {selectedConversation ? (
            <>
              {/* Chat Header - Light Theme - With Back Button */}
              <div className="flex-shrink-0 px-3 py-2.5 bg-white border-b border-gray-100 flex items-center gap-2">
                <button
                  onClick={handleBack}
                  className="p-2.5 -ml-1 rounded-lg hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Back to conversations"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>

                {/* Clickable seller info */}
                <button
                  onClick={handleViewSellerPage}
                  className="flex items-center gap-2 flex-1 min-w-0 hover:bg-gray-50 rounded-lg p-1 -m-1"
                >
                  <ProfilePicture
                    src={otherUser?.photoURL}
                    alt={getUserDisplayName(otherUser)}
                    size="sm"
                    className="w-9 h-9 rounded-full"
                    user={otherUser}
                    userId={otherUserId}
                  />
                  <div className="flex-1 min-w-0 text-left">
                    <h2 className="text-sm font-semibold text-gray-900 truncate">
                      {getUserDisplayName(otherUser)}
                    </h2>
                    <p className="text-[11px] text-gray-500 flex items-center gap-1">
                      {sellerPresence.isOnline ? (
                        <>
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                          Online
                        </>
                      ) : (
                        "Offline"
                      )}
                    </p>
                  </div>
                </button>

                {/* Menu button */}
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    className="p-1.5 rounded-lg hover:bg-gray-100"
                  >
                    <MoreVertical className="w-5 h-5 text-gray-500" />
                  </button>

                  {/* Dropdown menu */}
                  {showMenu && (
                    <div 
                      className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-md border border-gray-100 py-1"
                      style={{ zIndex: Z_INDEX.DROPDOWN }}
                    >
                      <button
                        onClick={handleViewSellerPage}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Store
                      </button>
                      <button
                        onClick={handleBlockUser}
                        disabled={isBlocking}
                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
                      >
                        <Ban className="w-4 h-4" />
                        {isBlocking ? "Blocking..." : "Block User"}
                      </button>
                    </div>
                  )}
                </div>

                {/* Close button - desktop */}
                <button
                  onClick={onClose}
                  className="hidden md:block p-1.5 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto bg-white min-h-0">
                <div className="max-w-2xl mx-auto px-3 py-4">
                  <MessageList
                    messages={messages}
                    currentUserId={currentUser.uid}
                    otherUser={messageListOtherUser}
                    onRetryMessage={retryFailedMessage}
                    onLoadMore={loadMoreMessages}
                    hasMore={hasMore}
                    loadingMore={loadingMore}
                    autoScroll={true}
                  />
                </div>
              </div>

              {/* Typing Indicator */}
              {isOtherUserTyping && (
                <div className="px-3 py-1.5 bg-white border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></span>
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                    </div>
                    <span className="text-xs text-gray-500">typing...</span>
                  </div>
                </div>
              )}

              {/* Message Input - Always visible at bottom with safe area */}
              <div 
                className="flex-shrink-0 bg-white border-t border-gray-200 shadow-lg relative" 
                style={{ 
                  paddingBottom: viewport.isMobile 
                    ? 'calc(64px + max(12px, env(safe-area-inset-bottom)))' // Account for bottom nav (64px) + safe area on mobile
                    : 'max(12px, calc(12px + env(safe-area-inset-bottom)))',
                  zIndex: 10000,
                  position: 'relative' as const,
                }}
              >
                <MessageInput
                  onSendMessage={handleSendMessage}
                  onTypingChange={handleTyping}
                  isSending={isSending}
                  placeholder="Type a message..."
                  disabled={!selectedConversation}
                />
              </div>
            </>
          ) : (
            <div className="hidden md:flex flex-col items-center justify-center h-full text-center bg-white p-6 relative">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-3 right-3 p-2.5 rounded-lg hover:bg-gray-100 text-gray-400 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close messaging drawer"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <MessageCircle className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="text-base font-medium text-gray-700 mb-1">
                Select a conversation
              </h2>
              <p className="text-sm text-gray-400">
                Choose from your existing chats
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Animate drawer and backdrop
  useEffect(() => {
    if (isOpen) {
      // Animate backdrop
      if (backdropRef.current) {
        gsap.fromTo(
          backdropRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.2, ease: "power2.out" }
        );
      }

      // Animate drawer
      if (drawerRef.current) {
        if (viewport.isDesktop) {
          gsap.fromTo(
            drawerRef.current,
            { x: '100%', opacity: 0 },
            { x: 0, opacity: 1, duration: 0.3, ease: "power2.out" }
          );
        } else {
          gsap.fromTo(
            drawerRef.current,
            { opacity: 0 },
            { opacity: 1, duration: 0.3, ease: "power2.out" }
          );
        }
      }
    } else {
      // Animate out
      if (backdropRef.current) {
        gsap.to(backdropRef.current, {
          opacity: 0,
          duration: 0.2,
          ease: "power2.in"
        });
      }

      if (drawerRef.current) {
        if (viewport.isDesktop) {
          gsap.to(drawerRef.current, {
            x: '100%',
            opacity: 0,
            duration: 0.25,
            ease: "power2.in"
          });
        } else {
          gsap.to(drawerRef.current, {
            opacity: 0,
            duration: 0.25,
            ease: "power2.in"
          });
        }
      }
    }
  }, [isOpen, viewport.isDesktop, widgetMode]);

  // Calculate drawer dimensions based on viewport with constraints
  const drawerStyle: React.CSSProperties = React.useMemo(() => {
    // Widget mode: No custom positioning needed, parent handles it
    if (widgetMode && viewport.isDesktop) {
      return {};
    }

    if (viewport.isDesktop) {
      // Desktop: Fixed width with viewport constraints
      const maxWidth = Math.min(
        DRAWER_SIZING.DESKTOP_MAX_WIDTH,
        viewport.width - DRAWER_SIZING.DESKTOP_MARGIN * 2
      );
      const minWidth = Math.max(
        DRAWER_SIZING.DESKTOP_MIN_WIDTH,
        Math.min(maxWidth, DRAWER_SIZING.DESKTOP_MIN_WIDTH)
      );
      
      // Ensure drawer fits in viewport
      const availableHeight = viewport.height - DRAWER_SIZING.DESKTOP_MARGIN * 2;
      const maxHeight = Math.min(availableHeight, viewport.height * 0.9);
      
      return {
        width: `${maxWidth}px`,
        maxWidth: `${DRAWER_SIZING.DESKTOP_MAX_WIDTH}px`,
        minWidth: `${minWidth}px`,
        height: `${maxHeight}px`,
        maxHeight: `${maxHeight}px`,
        top: `${DRAWER_SIZING.DESKTOP_MARGIN}px`,
        right: `${DRAWER_SIZING.DESKTOP_MARGIN}px`,
        bottom: 'auto',
        left: 'auto',
      };
    } else {
      // Mobile: Full screen with safe area insets
      // Use dvh (dynamic viewport height) for better mobile support
      const height = window.visualViewport?.height || viewport.height;
      
      return {
        width: '100vw',
        height: `${height}px`,
        maxHeight: `${height}px`,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        paddingTop: DRAWER_SIZING.MOBILE_SAFE_AREA_TOP,
        paddingBottom: DRAWER_SIZING.MOBILE_SAFE_AREA_BOTTOM,
        paddingLeft: DRAWER_SIZING.MOBILE_SAFE_AREA_LEFT,
        paddingRight: DRAWER_SIZING.MOBILE_SAFE_AREA_RIGHT,
      };
    }
  }, [viewport.isDesktop, viewport.width, viewport.height, widgetMode]);

  if (!isOpen) return null;

  // Widget mode: Render content only without backdrop and custom positioning
  if (widgetMode && viewport.isDesktop) {
    return (
      <div
        ref={drawerRef}
        className="h-full w-full overflow-hidden"
        style={{
          borderRadius: '0 0 0.5rem 0.5rem',
        }}
      >
        {renderContent()}
      </div>
    );
  }

  return (
    <>
      {/* Backdrop - Only show in drawer mode */}
      {!widgetMode && (
        <div
          ref={backdropRef}
          onClick={onClose}
          onTouchMove={(e) => {
            // Prevent scrolling on backdrop
            e.preventDefault();
          }}
          className="fixed inset-0 bg-black/30"
          style={{ 
            zIndex: Z_INDEX.BACKDROP,
            touchAction: 'none', // Prevent touch scrolling
          }}
          aria-hidden="true"
        />
      )}

      {/* Drawer - Viewport-aware positioning */}
      <div
        ref={drawerRef}
        className={widgetMode ? "relative bg-white overflow-hidden h-full w-full" : "fixed bg-white shadow-2xl overflow-hidden"}
        style={{
          ...drawerStyle,
          zIndex: widgetMode ? 'auto' : Z_INDEX.DRAWER,
          borderRadius: widgetMode ? '0' : (viewport.isDesktop ? '0.75rem 0 0 0.75rem' : '0'),
        }}
        role={widgetMode ? "region" : "dialog"}
        aria-modal={widgetMode ? "false" : "true"}
        aria-label="Messaging drawer"
        onClick={(e) => e.stopPropagation()}
      >
        {renderContent()}
      </div>
    </>
  );
};

export default MessagingDrawer;

